/**
 * AgentSummaryStack - Shows completed agent summaries in a readable stacked format
 */

import { useState } from 'react'
import {
  Search, Users, Map, FileText, Palette, Code, Image, Globe,
  ChevronDown, ChevronUp, CheckCircle2, Copy, Check, Sparkles, Coins,
  Clock, DollarSign, Zap, ImageIcon
} from 'lucide-react'
import { useStore } from '@store/useStore'
import { PHASES } from '@agents/orchestrator'
import { getStatsSummary } from '@services/tokenStats'

// Phase order for display
const PHASE_ORDER = [
  PHASES.SERP,
  PHASES.STRATEGE,
  PHASES.IDENTITE,
  PHASES.ARCHITECTE,
  PHASES.REDACTEUR,
  PHASES.DA,
  PHASES.CONTENT,
  PHASES.IMAGES,
]

// Agent identities
const AGENT_CONFIG = {
  [PHASES.SERP]: {
    name: 'SERP Analyzer',
    icon: Globe,
    color: 'from-cyan-400 to-teal-500',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/30',
    textColor: 'text-cyan-400',
  },
  [PHASES.STRATEGE]: {
    name: 'Stratege',
    icon: Search,
    color: 'from-blue-500 to-indigo-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-400',
  },
  [PHASES.IDENTITE]: {
    name: 'Identite',
    icon: Users,
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-400',
  },
  [PHASES.ARCHITECTE]: {
    name: 'Architecte',
    icon: Map,
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    textColor: 'text-emerald-400',
  },
  [PHASES.REDACTEUR]: {
    name: 'Brief SEO',
    icon: FileText,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    textColor: 'text-amber-400',
  },
  [PHASES.DA]: {
    name: 'DA',
    icon: Palette,
    color: 'from-rose-500 to-red-500',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30',
    textColor: 'text-rose-400',
  },
  [PHASES.CONTENT]: {
    name: 'Content',
    icon: Code,
    color: 'from-indigo-500 to-violet-500',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-400',
  },
  [PHASES.IMAGES]: {
    name: 'Images',
    icon: Image,
    color: 'from-green-500 to-lime-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-400',
  },
}

/**
 * Render agent-specific expanded content based on phase type
 */
function ExpandedContent({ phase, summary, config }) {
  const renderSources = (sources) => (
    <div className="space-y-1">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Sources E-E-A-T</div>
      {sources?.slice(0, 4).map((url, i) => (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="block text-xs text-cyan-400 hover:text-cyan-300 truncate"
        >
          🔗 {url.replace(/^https?:\/\//, '').slice(0, 40)}...
        </a>
      ))}
    </div>
  )

  const renderColors = (colors) => (
    <div className="flex gap-2 mt-2">
      {colors?.primary && (
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.primary }} />
          <span className="text-[10px] text-slate-400">{colors.primary}</span>
        </div>
      )}
      {colors?.accent && (
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.accent }} />
          <span className="text-[10px] text-slate-400">{colors.accent}</span>
        </div>
      )}
    </div>
  )

  const renderPages = (pages) => {
    const getTierColor = (tier) => {
      if (tier === 'T1' || tier === 1) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
      if (tier === 'T2' || tier === 2) return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      if (tier === 'T3' || tier === 3) return 'bg-amber-500/20 text-amber-400 border-amber-500/30'
      return 'bg-slate-700 text-slate-400 border-slate-600'
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-slate-500 uppercase tracking-wide">Pages à générer</div>
          <div className="text-[10px] font-bold text-emerald-400">{pages?.length || 0} total</div>
        </div>
        <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
          {pages?.map((p, i) => (
            <span
              key={i}
              className={`text-[10px] px-1.5 py-0.5 rounded border ${getTierColor(p.tier)}`}
              title={`${p.file} (${p.tier})`}
            >
              {p.name || p.file}
            </span>
          ))}
        </div>
        {/* Legend */}
        <div className="flex gap-2 text-[9px] text-slate-500 pt-1 border-t border-slate-700/50">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500/50"></span>T1</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-500/50"></span>T2</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-500/50"></span>T3</span>
        </div>
      </div>
    )
  }

  const renderKeywords = (keywords) => (
    <div className="space-y-1">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Mots-clés</div>
      <div className="flex flex-wrap gap-1">
        {keywords?.slice(0, 5).map((kw, i) => (
          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">
            {kw}
          </span>
        ))}
      </div>
    </div>
  )

  const renderFiles = (files) => (
    <div className="space-y-1">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Fichiers</div>
      <div className="grid grid-cols-2 gap-1">
        {files?.slice(0, 6).map((f, i) => (
          <span key={i} className="text-[10px] text-indigo-400 font-mono truncate">
            📄 {f}
          </span>
        ))}
      </div>
    </div>
  )

  // Phase-specific rendering
  switch (phase) {
    case PHASES.SERP:
      return summary.sources?.length > 0 ? renderSources(summary.sources) : null

    case PHASES.STRATEGE:
      return (
        <div className="space-y-2">
          {summary.siteType && (
            <div className="text-xs">
              <span className="text-slate-500">Type:</span>{' '}
              <span className={`font-medium ${config.textColor}`}>{summary.siteType}</span>
            </div>
          )}
          {summary.target && (
            <div className="text-xs">
              <span className="text-slate-500">Cible:</span>{' '}
              <span className="text-slate-300">{summary.target}</span>
            </div>
          )}
          {summary.usp && (
            <div className="text-xs text-slate-400 italic line-clamp-2">"{summary.usp}"</div>
          )}
        </div>
      )

    case PHASES.IDENTITE:
      return (
        <div className="space-y-2">
          {summary.persona?.name && (
            <div className="text-sm font-medium text-purple-300">{summary.persona.name}</div>
          )}
          {summary.persona?.profile && (
            <div className="text-xs text-slate-400">{summary.persona.profile}</div>
          )}
          {summary.tone && (
            <div className="text-xs">
              <span className="text-slate-500">Ton:</span>{' '}
              <span className="text-purple-400">{summary.tone}</span>
            </div>
          )}
          {summary.backstory && (
            <div className="text-[10px] text-slate-500 italic line-clamp-2">"{summary.backstory}"</div>
          )}
        </div>
      )

    case PHASES.ARCHITECTE:
      return summary.pages?.length > 0 ? renderPages(summary.pages) : null

    case PHASES.REDACTEUR:
      return summary.keywords?.length > 0 ? renderKeywords(summary.keywords) : null

    case PHASES.DA:
      return (
        <div className="space-y-2">
          {summary.colors && renderColors(summary.colors)}
          {summary.fonts && (
            <div className="text-xs text-slate-400">
              <span className="text-rose-400">{summary.fonts.heading}</span>
              {' / '}
              <span className="text-rose-300">{summary.fonts.body}</span>
            </div>
          )}
        </div>
      )

    case PHASES.CONTENT:
      return summary.files?.length > 0 ? renderFiles(summary.files) : null

    case PHASES.IMAGES:
      return summary.images?.length > 0 ? (
        <div className="text-xs text-slate-400">
          {summary.images.map(img => img.name || img).join(', ')}
        </div>
      ) : null

    default:
      return null
  }
}

// Single summary card - compact and visually striking
function SummaryCard({ phase, summary, isExpanded, onToggle }) {
  const [copied, setCopied] = useState(false)
  const config = AGENT_CONFIG[phase]
  if (!config || !summary) return null

  const Icon = config.icon

  const handleCopy = (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(JSON.stringify(summary, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Get bullets from summary (AI-generated)
  const bullets = summary.bullets || summary.highlights || []
  const keyMetric = summary.keyMetric

  // Check if we have rich expanded content
  const hasRichContent = summary.sources || summary.pages || summary.colors || summary.files || summary.keywords || summary.persona

  return (
    <div
      className={`rounded-xl border ${config.borderColor} overflow-hidden transition-all cursor-pointer hover:scale-[1.01] ${
        isExpanded ? 'bg-slate-800/50' : config.bgColor
      }`}
      onClick={onToggle}
    >
      {/* Header with gradient accent */}
      <div className={`h-1 bg-gradient-to-r ${config.color}`} />

      <div className="p-3">
        {/* Top row: icon + name + check */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
            <Icon className="w-3.5 h-3.5 text-white" />
          </div>
          <span className={`font-semibold text-sm ${config.textColor}`}>{config.name}</span>
          <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
          {isExpanded ? (
            <ChevronUp className="w-3 h-3 text-slate-500" />
          ) : (
            <ChevronDown className="w-3 h-3 text-slate-500" />
          )}
        </div>

        {/* Headline */}
        <p className={`text-sm font-medium text-white mb-2 ${isExpanded ? '' : 'line-clamp-2'}`}>
          {summary.headline || 'Terminé'}
        </p>

        {/* Key metric badge + tokens row */}
        <div className="flex items-center gap-2 flex-wrap">
          {keyMetric && (
            <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${config.bgColor} border ${config.borderColor}`}>
              <span className="text-slate-400">{keyMetric.label}:</span>
              <span className={`font-bold ${config.textColor}`}>{keyMetric.value}</span>
            </div>
          )}

          {/* Token stats (always visible if available) */}
          {summary.tokens && (
            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-slate-800/50 border border-slate-700/50">
              <Coins className="w-3 h-3 text-amber-400" />
              <span className="text-slate-500">{summary.tokens.input.toLocaleString()}</span>
              <span className="text-slate-600">→</span>
              <span className="text-slate-400">{summary.tokens.output.toLocaleString()}</span>
              <span className="text-amber-400/70 ml-1">${summary.tokens.cost.toFixed(3)}</span>
            </div>
          )}
        </div>

        {/* Expanded content - Agent-specific rich content OR bullets fallback */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-slate-700/50 space-y-2">
            {/* Try rich content first */}
            {hasRichContent && <ExpandedContent phase={phase} summary={summary} config={config} />}

            {/* Fallback to bullets if no rich content */}
            {!hasRichContent && bullets.length > 0 && (
              <div className="space-y-1.5">
                {bullets.slice(0, 4).map((bullet, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className={`${config.textColor} mt-0.5`}>
                      {bullet.match(/^[^\s]+/)?.[0] || '•'}
                    </span>
                    <span className="text-slate-300">{bullet.replace(/^[^\s]+\s*/, '')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Token details when expanded */}
            {summary.tokens && (
              <div className="flex items-center gap-3 text-[10px] text-slate-500 pt-2 border-t border-slate-700/30">
                <span className="font-mono">{summary.tokens.model}</span>
                <span>
                  <span className="text-slate-600">in:</span> {summary.tokens.input.toLocaleString()}
                </span>
                <span>
                  <span className="text-slate-600">out:</span> {summary.tokens.output.toLocaleString()}
                </span>
                <span className="text-amber-400/60">
                  ${summary.tokens.cost.toFixed(4)}
                </span>
              </div>
            )}

            {/* Copy button */}
            <button
              onClick={handleCopy}
              className="mt-2 flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copié !' : 'Copier JSON'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Total cost summary card - shown when all phases complete
function TotalSummaryCard({ summaries }) {
  const stats = getStatsSummary()
  const [expanded, setExpanded] = useState(false)

  // Calculate image costs (estimate based on provider)
  const imagesSummary = summaries[PHASES.IMAGES]
  const imagesGenerated = imagesSummary?.generated || imagesSummary?.images?.length || 0
  const imageProvider = imagesSummary?.images?.[0]?.provider || 'gemini'
  const imageCostPerUnit = imageProvider === 'dalle' ? 0.04 : 0.03 // medium quality avg
  const imageCost = imagesGenerated * imageCostPerUnit

  const totalCost = stats.total.cost + imageCost

  return (
    <div
      className="rounded-xl border border-amber-500/30 overflow-hidden bg-gradient-to-br from-amber-500/5 to-orange-500/5 cursor-pointer hover:scale-[1.01] transition-all"
      onClick={() => setExpanded(!expanded)}
    >
      {/* Header gradient */}
      <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />

      <div className="p-4">
        {/* Title */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <DollarSign className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-amber-400">Résumé des coûts</span>
          <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </div>

        {/* Main stats grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total cost */}
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-[10px] text-slate-500 uppercase">Coût total</span>
            </div>
            <div className="text-xl font-bold text-amber-400">${totalCost.toFixed(3)}</div>
          </div>

          {/* Duration */}
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-[10px] text-slate-500 uppercase">Durée</span>
            </div>
            <div className="text-xl font-bold text-blue-400">
              {Math.floor(stats.duration / 60)}:{String(stats.duration % 60).padStart(2, '0')}
            </div>
          </div>

          {/* Tokens */}
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Zap className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-[10px] text-slate-500 uppercase">Tokens</span>
            </div>
            <div className="text-lg font-bold text-purple-400">
              {stats.totalTokens.toLocaleString()}
            </div>
          </div>

          {/* Images */}
          <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center gap-1.5 mb-1">
              <ImageIcon className="w-3.5 h-3.5 text-green-400" />
              <span className="text-[10px] text-slate-500 uppercase">Images</span>
            </div>
            <div className="text-lg font-bold text-green-400">{imagesGenerated}</div>
          </div>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-slate-700/50 space-y-3">
            {/* Cost breakdown */}
            <div>
              <div className="text-[10px] text-slate-500 uppercase mb-2">Détail des coûts</div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">LLM (texte)</span>
                  <span className="text-slate-300 font-mono">${stats.total.cost.toFixed(4)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Images ({imagesGenerated}x ~${imageCostPerUnit})</span>
                  <span className="text-slate-300 font-mono">${imageCost.toFixed(4)}</span>
                </div>
                <div className="flex justify-between text-xs pt-1 border-t border-slate-700/30">
                  <span className="text-amber-400 font-medium">Total</span>
                  <span className="text-amber-400 font-bold font-mono">${totalCost.toFixed(4)}</span>
                </div>
              </div>
            </div>

            {/* Per phase breakdown */}
            {stats.costPerPhase.length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 uppercase mb-2">Par phase</div>
                <div className="space-y-1">
                  {stats.costPerPhase.map(({ phase, cost, tokens, model }) => (
                    <div key={phase} className="flex items-center justify-between text-[10px]">
                      <span className="text-slate-400 capitalize">{phase}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 font-mono">{tokens.toLocaleString()} tok</span>
                        <span className="text-amber-400/70 font-mono">{cost}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Model usage */}
            {stats.costPerModel.length > 0 && (
              <div>
                <div className="text-[10px] text-slate-500 uppercase mb-2">Par modèle</div>
                <div className="space-y-1">
                  {stats.costPerModel.map(({ model, cost, tokens, calls }) => (
                    <div key={model} className="flex items-center justify-between text-[10px]">
                      <span className="text-slate-400 font-mono">{model}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500">{calls} appels</span>
                        <span className="text-amber-400/70 font-mono">{cost}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function AgentSummaryStack() {
  const { summaries, project, ui } = useStore()
  const [expandedPhase, setExpandedPhase] = useState(null)

  // Get completed summaries in order
  const completedSummaries = PHASE_ORDER
    .filter(phase => summaries[phase])
    .map(phase => ({ phase, summary: summaries[phase] }))

  const completedCount = completedSummaries.length
  const totalCount = PHASE_ORDER.length
  const progressPercent = (completedCount / totalCount) * 100
  const isRunning = ui.isLoading
  const isComplete = completedCount === totalCount && !isRunning

  return (
    <div className="flex flex-col h-full">
      {/* Header with integrated progress bar - STICKY */}
      <div className="sticky top-0 z-10 bg-slate-950 p-3 space-y-2 border-b border-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent-400" />
            <span className="text-xs font-medium text-slate-400">
              Specs générées
            </span>
          </div>
          <span className="text-xs text-accent-400 font-bold">
            {completedCount}/{totalCount}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-accent-500 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Domain */}
        {project.domain && (
          <div className="flex items-center justify-between text-[10px]">
            <span className="font-mono text-primary-400">{project.domain}</span>
            {isRunning && (
              <span className="text-slate-500 animate-pulse">en cours...</span>
            )}
          </div>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">

      {/* Empty state */}
      {completedCount === 0 && (
        <div className="py-6 flex flex-col items-center justify-center text-center">
          <div className="w-10 h-10 rounded-xl bg-slate-800/50 flex items-center justify-center mb-2">
            <Sparkles className="w-5 h-5 text-slate-600 animate-pulse" />
          </div>
          <p className="text-xs text-slate-500">
            Les résultats apparaîtront ici
          </p>
        </div>
      )}

      {/* Cards */}
      {completedSummaries.map(({ phase, summary }) => (
        <SummaryCard
          key={phase}
          phase={phase}
          summary={summary}
          isExpanded={expandedPhase === phase}
          onToggle={() => setExpandedPhase(expandedPhase === phase ? null : phase)}
        />
      ))}

      {/* Total cost summary - shown when generation is complete */}
      {isComplete && (
        <TotalSummaryCard summaries={summaries} />
      )}
      </div>
    </div>
  )
}
